import { useCallback, useRef, useState } from 'preact/hooks';
import { fetchOnce, prepareBody } from '@shared/hooks/http';
import { apiBase } from '../api/api-base';
import { toast } from 'react-toastify';

export function usePatch(
    endpoint,
    {
        asFormData = false,
        autoCancelPrevious = true,
        successMessage = 'Successfully captured data!',
    } = {}
) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    const controllerRef = useRef(null);

    const cancel = useCallback(() => {
        try {
            controllerRef.current?.abort();
        } finally {
            controllerRef.current = null;
        }
    }, []);

    const patch = useCallback(
        async (body, config = {}) => {
            if (autoCancelPrevious && controllerRef.current) {
                controllerRef.current.abort();
                controllerRef.current = null;
            }

            const controller = new AbortController();
            controllerRef.current = controller;

            setIsSubmitting(true);
            setError(null);
            setData(null);
            try {
                const rawBody = asFormData
                    ? (() => {
                          const fd = new FormData();
                          for (const [k, v] of Object.entries(body ?? {})) fd.append(k, v);
                          return fd;
                      })()
                    : body;

                const { body: prepared, headers } = prepareBody(rawBody, config.headers ?? {});

                const result = await fetchOnce(`${apiBase}${endpoint}`, {
                    method: 'PATCH',
                    credentials: 'include',
                    headers,
                    body: prepared,
                    signal: config.signal ?? controller.signal,
                });

                const resolved = result ?? { success: true };
                setData(resolved);
                toast.success(successMessage);
                return resolved;
            } catch (err) {
                if (err?.name === 'AbortError') return Promise.reject(err);
                toast.error(err?.body?.detail ?? err?.message ?? 'Something went wrong!');
                setError(err);
                throw err;
            } finally {
                setIsSubmitting(false);
            }
        },
        [endpoint, asFormData, autoCancelPrevious]
    );

    return { patch, cancel, isSubmitting, error, data };
}
